import { ChangeEvent, RefObject } from 'react';
import { twMerge } from 'tailwind-merge';
import Spinner from '../spinner/spinner';

export type Option = {
    value: string;
    label: string;
};

export interface SelectProps {
    ref?: RefObject<HTMLSelectElement>;
    id?: string;
    name: string;
    options: Option[];
    value?: string;
    disabled?: boolean;
    onChange?: (value: ChangeEvent<HTMLSelectElement>) => void;
    isLoading?: boolean;
    spinner?: {
        size?: number;
        className?: string;
        color?: 'default' | 'primary' | 'secondary' | 'variant';
    };
    label?: string;
    placeholder?: string;
    className?: string;
    selectClassName?: string;
    multiple?: boolean;
    firstOptionEmpty?: boolean;
}

function ArrowDownIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
            <path d="M12.354 15.354l-6-6a.5.5 0 01.708-.708L12 13.293l5.646-5.647a.5.5 0 01.708.708l-6 6a.5.5 0 01-.708 0z" />
        </svg>
    );
}

export function Select({
    ref,
    id,
    name,
    options,
    value,
    onChange,
    disabled,
    isLoading = false,
    spinner,
    label,
    placeholder,
    className,
    selectClassName,
    firstOptionEmpty = true,
    multiple = false,
}: SelectProps) {
    const arrowClasses = twMerge('flex ml-auto absolute right-4 peer-open:rotate-180');
    const containerClasses = twMerge('flex justify-end items-center relative', className);
    const selectClasses = twMerge(
        'form-select',
        'block w-full px-3 py-1.5   text-base',
        'font-normal text-gray-700 bg-white bg-clip-padding bg-no-repeat',
        'border border-solid border-gray-100 rounded transition ease-in-out',
        'm-0 focus:text-gray-700 focus:bg-white focus:border-primary-500 focus:outline-none peer',
        className,
        containerClasses,
    );
    return (
        <div className={containerClasses}>
            <select
                ref={ref}
                id={id}
                name={name}
                value={value}
                onChange={(event) => {
                    onChange && onChange(event);
                }}
                disabled={disabled}
                className={selectClasses}
                multiple={multiple}
            >
                {firstOptionEmpty && <option value="">{placeholder || label || ''}</option>}
                {options &&
                    options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
            </select>
            {isLoading && (
                <Spinner
                    size={spinner?.size || 0}
                    className={`absolute m-2 ${spinner?.className}`}
                    color={spinner?.color || 'primary'}
                />
            )}
        </div>
    );
}

export default Select;
